import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  ReactNode,
} from 'react'
import React, { useMemo } from 'react'

import classnames from 'classnames'

import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import { ScrollableWrapper } from './components/ScrollableWrapper'
import { Context } from './Table.context'
import styles from './Table.module.scss'

export const Table: FunctionComponent<Props> = ({
  title,
  caption,
  sizing,
  className,
  tableClassName,
  children,
  tableId,
  ...rest
}) => {
  const contextValue = useMemo(() => ({ sizing }), [sizing])

  return (
    <section
      data-testid="table"
      {...rest}
      className={classnames(className, styles.grid)}
    >
      {title && (
        <TypographyV2 textStyle="sb-text-interface-heading-large">
          {title}
        </TypographyV2>
      )}
      {caption && (
        <TypographyV2 textStyle="sb-text-interface-body-small-regular">
          {caption}
        </TypographyV2>
      )}

      <ScrollableWrapper>
        <table
          className={classnames(styles.table, tableClassName)}
          id={tableId}
        >
          <Context.Provider value={contextValue}>{children}</Context.Provider>
        </table>
      </ScrollableWrapper>
    </section>
  )
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Title of the table that shows at the top.
   */
  title?: string

  /**
   * Optional caption that shows below the title and above the table.
   */
  caption?: string

  /**
   * Optional class name for augmenting the table class attribute.
   */
  className?: string

  /**
   * Optional class name for the table element itself.
   */
  tableClassName?: string

  /**
   * List of sizes for each of the columns.
   */
  sizing?: Array<Sizing>
  /**
   * Table id
   */
  tableId?: string
  children: ReactNode
} & ComponentPropsWithoutRef<'section'>

export type Sizing = 'x-small' | 'small' | 'medium' | 'large'
